import "dayjs/locale/tr";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./components/ThemeContext";
import { lazy, Suspense, useEffect, useState, useCallback, memo } from "react";
import { DatesProvider } from "@mantine/dates";
import { ToastContainer } from "react-toastify";
import { useSetAtom } from "jotai";
import { meAtom } from "./store/auth";
import ScrollToTop from "./components/ScrollToTop";
import authService from "./services/api/authService";
import { TourProvider, type StepType } from "@reactour/tour";
import {
  canAttemptAccessRefresh,
  clearTokenData,
  getRefreshToken,
  saveTokenDataRefresh,
} from "./services/api/tokenManager";
import AppointmentsCalendarPage from "./pages/profile/Appointment/AppointmentsCalendarPage";
import ContactMessages from "./pages/panel/ContactMessages";
import ForgotPasswordPage from "./pages/registeration/ForgotPasswordPage";

// Suspense/Tour gibi provider'lara stable props vermek için sabit props
const EMPTY_TOUR_STEPS: StepType[] = [];
const DATES_SETTINGS = { locale: "tr", firstDayOfWeek: 1 } as const;

export const Main = lazy(() => import("./layouts/Main"));
export const Error404 = lazy(() => import("./pages/error/Error404"));
export const Home = lazy(() => import("./pages/main/Home"));
export const WhoWeAre = lazy(() => import("./pages/about-us/WhoWeAre"));
export const WhatWeProvidePage = lazy(
  () => import("./pages/about-us/WhatWeProvidePage")
);
export const ContactPage = lazy(() => import("./pages/contact/contact"));
export const LoginPage = lazy(() => import("./pages/registeration/LoginPage"));
export const RegisterPage = lazy(
  () => import("./pages/registeration/RegisterPage")
);
export const MailActivation = lazy(
  () => import("./pages/registeration/MailActivation")
);
export const GuestProtectedRoutes = lazy(
  () => import("./layouts/Protecteds/GuestProtectedRoutes")
);
export const AuthProtectedRoutes = lazy(
  () => import("./layouts/Protecteds/AuthProtectedRoutes")
);
export const PageCreation = lazy(
  () => import("./pages/packageCreation/PageCreation")
);
export const ReadyTemplates = lazy(
  () => import("./pages/ready-templates/ReadyTemplates")
);
export const ProfileLayout = lazy(() => import("./layouts/ProfileLayout"));
export const CustomerPanel = lazy(
  () => import("./pages/profile/CustomerPanel")
);
export const SalonInfo = lazy(
  () => import("./pages/profile/Salon/SalonInformations")
);
export const UpdateSalon = lazy(
  () => import("./pages/profile/Salon/UpdateSalon")
);
export const AccountingPage = lazy(
  () => import("./pages/profile/Accounting/AccountingPage")
);
export const InventoryPage = lazy(
  () => import("./pages/profile/Inventory/InventoryPage")
);
export const ServicesPage = lazy(
  () => import("./pages/profile/Service/ServicesPage")
);
export const EmployeesPage = lazy(
  () => import("./pages/profile/Employee/EmployeesPage")
);
export const CreateTransactionPage = lazy(
  () => import("./pages/profile/Accounting/CreateTransactionPage")
);
export const CreateProductPage = lazy(
  () => import("./pages/profile/Inventory/CreateProductPage")
);
export const CreateServicePage = lazy(
  () => import("./pages/profile/Service/CreateServicePage")
);
export const CreateAppointmentPage = lazy(
  () => import("./pages/profile/Appointment/CreateAppointmentPage")
);
export const CreateEmployeePage = lazy(
  () => import("./pages/profile/Employee/CreateEmployeePage")
);
export const Packages = lazy(() => import("./pages/packages/Packages"));
export const EditProductPage = lazy(
  () => import("./pages/profile/Inventory/EditProductPage")
);
export const EditServicePage = lazy(
  () => import("./pages/profile/Service/EditServicePage")
);
export const MonthlyFinanceReportPage = lazy(
  () => import("./pages/profile/Accounting/MonthlyFinanceReportPage")
);
export const AssignPermissionsPage = lazy(
  () => import("./pages/profile/Employee/AssignPermissionsPage")
);
export const CustomerListPage = lazy(
  () => import("./pages/profile/Customer/CustomerListPage")
);
export const ActivateSalon = lazy(() => import("./pages/panel/ActivateSalon"));
export const PanelLayout = lazy(() => import("./layouts/PanelLayout"));
export const CustomTemplatePage = lazy(
  () => import("./pages/custom-templates/CustomTemplatePage")
);
export const ControlPanel = lazy(() => import("./pages/profile/ControlPanel"));
export const Loading = lazy(() => import("./pages/loading/Loading"));
export const ForWhomPage = lazy(() => import("./pages/use/ForWhomPage"));
export const HowToUse = lazy(() => import("./pages/use/howToUse"));
export const Features = lazy(() => import("./pages/use/features"));
export const PrivacyPolicy = lazy(
  () => import("./pages/Policies/PrivacyPolicy")
);
export const DeliveryRefundPolicy = lazy(
  () => import("./pages/Policies/DeliveryRefundPolicy")
);
export const StatisticsDashboard = lazy(
  () => import("./pages/profile/StatisticsDashboard")
);
export const UserListPage = lazy(() => import("./pages/panel/UserListPage"));
export const PWIPage = lazy(() => import("./pages/PWIPage/PWIPage"));
export const SalonStatusProtectedRoutes = lazy(
  () => import("./layouts/Protecteds/SalonStatusProtectedRoutes")
);
export const DistanceSalesContract = lazy(
  () => import("./pages/Policies/DistanceSalesContract")
);
export const KVKKClarificationText = lazy(
  () => import("./pages/Policies/KVKKClarificationText")
);
export const Policies = lazy(() => import("./pages/Policies/Policies"));

function App() {
  const setMe = useSetAtom(meAtom);
  const [loading, setLoading] = useState(true);
  const logOut = useCallback(async () => {
    await authService.logOut(getRefreshToken()!);
    clearTokenData();
    setMe(null);
  }, [setMe]);
  const getMe = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokenData();
        return;
      }

      if (canAttemptAccessRefresh()) {
        const newTokens = await authService.refreshToken(refreshToken);
        saveTokenDataRefresh(newTokens);
      }

      const me = await authService.me();
      setMe(me);
    } catch (err) {
      logOut();
    } finally {
      setLoading(false);
    }
  }, [setMe, logOut]);
  useEffect(() => {
    getMe();
  }, [getMe]);

  return (
    <ThemeProvider>
      {loading ? (
        <Loading />
      ) : (
        <TourProvider steps={EMPTY_TOUR_STEPS}>
          <DatesProvider settings={DATES_SETTINGS}>
            <Suspense fallback={<Loading />}>
              <div className="App min-h-screen">
                <BrowserRouter>
                  <ScrollToTop />
                  <AppRoutes />
                </BrowserRouter>
                {/* <CookieConsent /> */}
                <ToastContainer limit={3} />
              </div>
            </Suspense>
          </DatesProvider>
        </TourProvider>
      )}
    </ThemeProvider>
  );
}

const AppRoutes = memo(function AppRoutes() {
  return (
    <Routes>
      <Route element={<Main />}>
        <Route path="/" element={<Home />} />
        <Route path="/biz-kimiz" element={<WhoWeAre />} />
        <Route path="/ne-sagliyoruz" element={<WhatWeProvidePage />} />
        <Route path="/hazir-sablonlar" element={<ReadyTemplates />} />
        <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
        <Route
          path="/teslimat-ve-iade-politikasi"
          element={<DeliveryRefundPolicy />}
        />
        <Route
          path="/mesafeli-satis-sozlesmesi"
          element={<DistanceSalesContract />}
        />
        <Route path="/sozlesmeler" element={<Policies />} />
        <Route
          path="/kvkk-kapsaminda-aydinlatma-metni"
          element={<KVKKClarificationText />}
        />
        <Route path="/kimler-icin" element={<ForWhomPage />} />
        <Route path="/nasil-kullanilir" element={<HowToUse />} />
        <Route path="/ozellikler" element={<Features />} />
        <Route path="/iletisim" element={<ContactPage />} />
        <Route path="/paketlerimiz" element={<Packages />} />

        {/* Giriş yapmış kullanıcı GİREMEZ */}
        <Route element={<GuestProtectedRoutes />}>
          <Route path="/giris-yap" element={<LoginPage />} />
          <Route path="/kayit-ol" element={<RegisterPage />} />
          <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
          <Route path="/sign/reset-password" element={<ForgotPasswordPage />} />
          <Route path="/telefon-aktivasyon" element={<MailActivation />} />
        </Route>

        {/* Giriş yapmamış kullanıcı GİREMEZ */}
        <Route element={<AuthProtectedRoutes />}>
          <Route path="/salon-olustur" element={<PageCreation />} />
          <Route path="/odeme" element={<PWIPage />} />
          <Route path="/ozel-sablon" element={<CustomTemplatePage />} />

          <Route path="/profil" element={<ProfileLayout />}>
            <Route index element={<CustomerPanel />} />
            <Route path="kontrol-paneli" element={<ControlPanel />} />
            <Route path="salon-bilgileri" element={<SalonInfo />} />

            <Route element={<SalonStatusProtectedRoutes />}>
              <Route
                path="istatistik-paneli"
                element={<StatisticsDashboard />}
              />
              <Route
                path="salonu-duzenle/:salonCode"
                element={<UpdateSalon />}
              />
              <Route path="muhasebe" element={<AccountingPage />} />
              <Route path="muhasebe/ekle" element={<CreateTransactionPage />} />
              <Route
                path="aylık-rapor"
                element={<MonthlyFinanceReportPage />}
              />
              <Route path="envanter" element={<InventoryPage />} />
              <Route path="envanter/ekle" element={<CreateProductPage />} />
              <Route
                path="envanter/duzenle/:code"
                element={<EditProductPage />}
              />
              <Route path="randevular" element={<AppointmentsCalendarPage />} />
              <Route
                path="randevular/ekle"
                element={<CreateAppointmentPage />}
              />
              <Route path="müşterilerim" element={<CustomerListPage />} />
              <Route path="çalışanlar" element={<EmployeesPage />} />
              <Route path="çalışanlar/ekle" element={<CreateEmployeePage />} />
              <Route
                path="çalışanlar/yetkiler/:code"
                element={<AssignPermissionsPage />}
              />
              <Route path="hizmetler" element={<ServicesPage />} />
              <Route path="hizmetler/ekle" element={<CreateServicePage />} />
              <Route
                path="hizmetler/duzenle/:code"
                element={<EditServicePage />}
              />
            </Route>
          </Route>

          <Route path="/panel" element={<PanelLayout />}>
            <Route index element={<ActivateSalon />} />
            <Route path="users" element={<UserListPage />} />
            <Route path="messages" element={<ContactMessages />} />
          </Route>
        </Route>
      </Route>

      {/* 404 */}
      <Route element={<Main />}>
        <Route path="*" element={<Error404 />} />
      </Route>
    </Routes>
  );
});

export const subscriptionPanelSteps = [
  {
    selector: "#aktif-paket",
    content:
      "Buradan şu anda kullandığınız paketi, bitiş tarihini ve son ödeme gününü takip edebilirsiniz.",
  },
  {
    selector: "#sms-hakki",
    content:
      "Bu alanda kalan SMS hakkınızı görebilirsiniz. SMS limitiniz dolduğunda artırmak için 'SMS Hakkı Yükselt' butonunu kullanabilirsiniz.",
  },
  {
    selector: "#eleman-hakki",
    content:
      "Salonunuzda kaç çalışan tanımlayabileceğinizi ve şu an kaç çalışanın sistemde olduğunu burada görebilirsiniz.",
  },
  {
    selector: "#fotograf-hakki",
    content:
      "Yükleyebileceğiniz fotoğraf sayısını bu alanda takip edebilirsiniz. Limit dolarsa yeni hak satın alabilirsiniz.",
  },
  {
    selector: "#paket-yonetimi",
    content:
      "Bu bölümde paketinizi yükselterek SMS, çalışan veya fotoğraf gibi özelliklerinizi artırabilirsiniz.",
  },
  {
    selector: "#paketi-yukselt",
    content:
      "Paketinizi doğrudan buradan yükseltebilirsiniz. Daha fazla özellik için tıklayın!",
  },
];

export const statisticsPageSteps = [
  {
    selector: "#randevu-sayisi",
    content:
      "Bu kutucukta bu ayki toplam randevu sayısını görebilirsiniz. Ayrıca bir önceki aya oranını da takip edebilirsiniz.",
  },
  {
    selector: "#iptal-randevu-sayisi",
    content:
      "Bu kutucukta iptal edilen randevu sayısını ve bir önceki aya göre iptal oranını takip edebilirsiniz.",
  },
  {
    selector: "#yeni-musteri-sayisi",
    content:
      "Bu alanda ay içerisinde sisteme eklenen yeni müşteri sayısını görürsünüz. Ayrıca bir önceki aya oranını da takip edebilirsiniz.",
  },
  {
    selector: "#aylik-kazanc",
    content:
      "Bu kutu, bu ayki toplam kazancı göstermektedir. Negatif ya da pozitif olabilir. Ayrıca bir önceki aya oranını da takip edebilirsiniz.",
  },
  {
    selector: "#istatistik-tab-menu",
    content:
      "Bu sekmelerden randevu, iptal, gelir-gider ve ürün satış grafikleri arasında geçiş yapabilirsiniz.",
  },
  {
    selector: "#istatistik-grafik",
    content:
      "Seçili sekmeye göre detaylı istatistikleri bu grafik üzerinden görüntüleyebilirsiniz.",
  },
  {
    selector: "#randevu-dagilim-grafik",
    content:
      "Alt kısımdaki grafik randevuları kategori, çalışan veya ürün bazında dağılımını gösterir.",
  },
  {
    selector: "#gelir-dagilim-grafik",
    content:
      "Alt kısımdaki grafik gelir kaynaklarını kategori, çalışan veya ürün bazında dağılımını gösterir.",
  },
  {
    selector: "#en-iyiler",
    content:
      "Bu kutuda en çok randevu alan çalışan, en çok tercih edilen hizmet ve en çok satılan ürün bilgileri yer alır.",
  },
  {
    selector: "#en-dusukler",
    content:
      "Burada en az tercih edilen çalışan, hizmet ve ürün bilgilerini bulabilirsiniz.",
  },
  {
    selector: "#kritik-urunler",
    content:
      "Bu alanda stoğu azalan kritik ürünler listelenir. Müdahale etmek için ideal bir yerdir.",
  },
];

export const appointmentSteps = [
  {
    selector: "#new-appointment-button",
    content:
      "Yeni bir randevu eklemek için bu butona tıklayın. Müşteri bilgisi, çalışan, hizmet ve saat bilgilerini girerek hızlıca oluşturabilirsiniz.",
  },
  {
    selector: "#filter-appointment-button",
    content:
      "Bu buton sayesinde belirli durum, çalışan, müşteri gibi filtrelerle veya arama yaparak randevuları kolayca bulabilirsiniz.",
  },
  {
    selector: "#sort-appointment-button",
    content:
      "Randevuları tarihe göre sıralamak için bu butonu kullanabilirsiniz.",
  },
];

export const salonInfoSteps = [
  {
    selector: "#salon-name-area",
    content:
      "Salon adınız, açıklama ve türü burada görüntülenir. Bu bilgiler kullanıcıların sizi daha kolay tanımasını sağlar.",
  },
  {
    selector: "#salon-public-url",
    content:
      "Bu bağlantı, müşterilerinizin görüntüleyeceği sayfanızın adresidir. ",
  },
  {
    selector: "#salon-public-url-appointment",
    content:
      "Bu bağlantı, müşterilerinizin doğrudan randevu alabileceği sayfadır. Tıklayarak kopyalayabilirsiniz.",
  },
  {
    selector: "#salon-qr-code",
    content:
      "QR kodu müşterilerinizle paylaşarak onları doğrudan randevu sayfanıza yönlendirebilirsiniz. Dilerseniz tıklayarak QR kodunu indirebilirsiniz.",
  },
  {
    selector: "#salon-working-hours",
    content:
      "Salonunuzun haftalık çalışma saatlerini buradan belirleyebilir ve müşterilerin ne zaman hizmet alabileceğini tanımlayabilirsiniz.",
  },
  {
    selector: "#salon-address-phone",
    content:
      "Adres ve telefon bilgilerinizi buradan düzenleyebilir, müşterilerin sizinle kolayca iletişim kurmasını sağlayabilirsiniz.",
  },
  {
    selector: "#salon-social-media",
    content:
      "Salonunuzun sosyal medya hesaplarına buradan kolayca erişebilirsiniz.",
  },
  {
    selector: "#salon-location-map",
    content:
      "Harita üzerinden salonunuzun konumunu gösterir. Doğru konum, müşterilerinizin sizi bulmasını kolaylaştırır.",
  },
];

export const accountingSteps = [
  {
    selector: "#cash-total-amount",
    content:
      "Kasada bulunan toplam tutarı burada görebilirsiniz. Tüm gerçekleştirilmiş işlemler bu tutarı etkiler ancak borç veya alacaklar kasa durumunda değişim yaratmaz.",
  },
  {
    selector: "#filter-button",
    content:
      "İşlemleri filtrelemek için bu butona tıklayın. Tür, kategori, durum ve tutar gibi kriterlerle listeyi daraltabilirsiniz.",
  },
  {
    selector: "#add-transaction-button",
    content:
      "Yeni gelir veya gider eklemek için bu butona tıklayın. Manuel girişler için kullanılır.",
  },
  {
    selector: "#transaction-table-row",
    content:
      "Bu alanda yapılan tüm işlemler listelenir. Tür, kategori, tutar, tarih ve not bilgilerini içerir.",
  },
];

export const monthlyReportSteps = [
  {
    selector: "#aylık-rapor",
    content:
      "Aylık Rapor sayfasına hoş geldiniz. Burada belirli bir yıla ve aya ait finansal özetleriniz bulunmaktadır.",
  },
];

export const inventorySteps = [
  {
    selector: "#envanter",
    content:
      "Bu sayfada ürünlerinizi ve stok miktarlarını yönetebilirsiniz. Envanter takibi yaparak eksilen ürünleri hızlıca görebilir ve müdahale edebilirsiniz.",
  },
  {
    selector: "#add-inventory-button",
    content:
      "Yeni bir ürün eklemek için bu butona tıklayın. Ürün adı, stok miktarı, açıklama gibi bilgileri girerek kaydedebilirsiniz.",
  },
];

export const customerSteps = [
  {
    selector: "#müşterilerim",
    content:
      "Bu sayfa, salonunuza kayıtlı tüm müşterileri görüntüleyebileceğiniz alandır. Hangi müşterilerin size geldiğini buradan takip edebilirsiniz.",
  },
];

export const employeeSteps = [
  {
    selector: "#çalışanlar",
    content:
      "Bu sayfa salonunuza kayıtlı tüm çalışanları listelediğiniz alandır. Çalışan bilgilerini görüntüleyebilir, düzenleyebilir ya da yeni çalışan ekleyebilirsiniz.",
  },
  {
    selector: "#add-employee-button",
    content:
      "Yeni bir çalışan eklemek için bu butona tıklayın. Ad, pozisyon, telefon, e-posta ve çalışma saatleri gibi bilgileri girmeniz yeterlidir.",
  },
];

export const serviceSteps = [
  {
    selector: "#hizmetler",
    content:
      "Bu sayfada salonunuza ait tüm hizmetleri tanımlayabilir ve düzenleyebilirsiniz. Müşterilerinizin randevu alırken seçeceği hizmetler burada listelenir.",
  },
  {
    selector: "#add-service-button",
    content:
      "Yeni bir hizmet eklemek için bu butona tıklayın. Hizmet adı, açıklaması, süresi ve fiyatı gibi bilgileri girmeniz yeterlidir.",
  },
];

export default App;
